/**
 * Schritt 4: Modellvergleich: Einkommensteuer vs. Abgeltungsteuer (Beispielrechnung) Tab
 * Vergleicht Kapitalertragsteuer vs. vereinfachte Einkommensteuer für optimale Besteuerung
 */

import React, { useMemo } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Alert,
  Box,
  ColumnLayout,
  Input,
  FormField,
  ExpandableSection
} from '@cloudscape-design/components';
import { PortfolioPosition, SteuerParameter } from '@/types';
import { PortfolioService } from '@/services/portfolioService';
import { formatCurrency } from '@/utils/formatters';
import { KIRCHENSTEUER_OPTIONEN, TAX_RATES, SOLI_THRESHOLDS_2025, SOLI_THRESHOLDS_2026, ESTG_2025, ESTG_2026, SPARERFREIBETRAEGE } from '@/utils/constants';
import { berechneNettoKapitalsteuerNachVerrechnung, portfolioPositionKey } from '@/utils/capitalGainsAggregation';
import { validateIncomeTax, devValidate } from '@/utils/taxValidation';

interface GuenstigerpruefungTabProps {
  portfolioPositionen: PortfolioPosition[];
  steuerParameter: SteuerParameter | null;
  jahreseinkommen: string;
  sonderausgaben: string;
  onJahreseinkommenUpdate: (jahreseinkommen: string) => void;
  onSonderausgabenUpdate: (sonderausgaben: string) => void;
  sparerPauschbetragEnabled: boolean;
}

/**
 * Detaillierte Einkommensteuer-Berechnung nach §32a EStG
 * Basiert auf den exakten BMF-Formeln mit Ehegattensplitting für verheiratete Paare
 * Verwendet 2026-Tarif für steuerjahr >= 2026, sonst 2025
 */
function calculateOfficialIncomeTaxDetailed(
  taxableIncome: number,
  familienstand: string,
  kirchensteuerSatz?: string,
  steuerjahr: number = 2026
): {
  einkommensteuer: number;
  solidaritaetszuschlag: number;
  kirchensteuer: number;
  gesamt: number;
} {
  let zvE = taxableIncome;
  let einkommensteuer = 0;

  const estg = steuerjahr >= 2026 ? ESTG_2026 : ESTG_2025;

  // Für verheiratete Paare: Ehegattensplitting anwenden
  if (familienstand === 'verheiratet') {
    zvE = taxableIncome / 2;
  }

  // Offizielle BMF-Formeln nach §32a EStG
  if (zvE <= estg.GRUNDFREIBETRAG) {
    einkommensteuer = 0;
  } else if (zvE <= estg.ZONE2_OBERGRENZE) {
    const y = (zvE - estg.GRUNDFREIBETRAG) / 10000;
    einkommensteuer = (estg.ZONE2_KOEFF * y + 1400) * y;
  } else if (zvE <= estg.ZONE3_OBERGRENZE) {
    const z = (zvE - estg.ZONE2_OBERGRENZE) / 10000;
    einkommensteuer = (estg.ZONE3_KOEFF * z + 2397) * z + estg.ZONE3_KONST;
  } else if (zvE <= estg.ZONE4_OBERGRENZE) {
    einkommensteuer = 0.42 * zvE - estg.ZONE4_KONST;
  } else {
    einkommensteuer = 0.45 * zvE - estg.ZONE5_KONST;
  }

  // Für verheiratete Paare: Ergebnis verdoppeln (Splitting-Verfahren)
  if (familienstand === 'verheiratet') {
    einkommensteuer = einkommensteuer * 2;
  }

  // Solidaritätszuschlag: Freigrenze und Gleitzone
  let solidaritaetszuschlag = 0;
  const soliThresholds = steuerjahr >= 2026 ? SOLI_THRESHOLDS_2026 : SOLI_THRESHOLDS_2025;
  const soliFreigrenze = familienstand === 'verheiratet'
    ? soliThresholds.FREIGRENZE_MARRIED
    : soliThresholds.FREIGRENZE_SINGLE;

  // §3 Abs. 3 SolZG: Freigrenze bezieht sich auf die festgesetzte ESt (Bemessungsgrundlage), nicht auf das zvE
  if (einkommensteuer > soliFreigrenze) {
    const abschmelzungsObergrenze = soliFreigrenze * soliThresholds.GLEITZONE_MULTIPLIER;

    if (einkommensteuer <= abschmelzungsObergrenze) {
      // §4 SolZG Gleitzone: 11,9% × (ESt − Freigrenze)
      solidaritaetszuschlag = (einkommensteuer - soliFreigrenze) * TAX_RATES.SOLIDARITAETSZUSCHLAG_GLEITZONE;
    } else {
      // Volle Anwendung: 5,5% der ESt
      solidaritaetszuschlag = einkommensteuer * TAX_RATES.SOLIDARITAETSZUSCHLAG;
    }
  }

  // Kirchensteuer basierend auf Einkommensteuer (nicht auf Einkommensteuer + Soli)
  let kirchensteuer = 0;
  if (kirchensteuerSatz && kirchensteuerSatz !== 'keine' && einkommensteuer > 0) {
    const kirchensteuerKonfig = KIRCHENSTEUER_OPTIONEN.find(k => k.key === kirchensteuerSatz);
    if (kirchensteuerKonfig) {
      const kirchensteuerProzent = kirchensteuerKonfig.prozentsatz / 100;
      kirchensteuer = einkommensteuer * kirchensteuerProzent;
    }
  }

  const gesamt = einkommensteuer + solidaritaetszuschlag + kirchensteuer;

  const result = {
    einkommensteuer: Math.round(einkommensteuer * 100) / 100,
    solidaritaetszuschlag: Math.round(solidaritaetszuschlag * 100) / 100,
    kirchensteuer: Math.round(kirchensteuer * 100) / 100,
    gesamt: Math.round(gesamt * 100) / 100
  };

  // Validate calculation in development mode
  devValidate(validateIncomeTax, taxableIncome, familienstand, kirchensteuerSatz, steuerjahr, {
    einkommensteuer: result.einkommensteuer,
    solidaritaetszuschlag: result.solidaritaetszuschlag,
    kirchensteuer: result.kirchensteuer,
    gesamt: result.gesamt,
    taxableIncome
  });

  return result;
}

/**
 * Offizielle Einkommensteuer-Berechnung nach §32a EStG
 */
function calculateOfficialIncomeTax(
  taxableIncome: number,
  familienstand: string,
  kirchensteuerSatz?: string,
  steuerjahr: number = 2026
): number {
  return calculateOfficialIncomeTaxDetailed(taxableIncome, familienstand, kirchensteuerSatz, steuerjahr).gesamt;
}

/**
 * Hauptkomponente für den Modellvergleich: Einkommensteuer vs. Abgeltungsteuer (Beispielrechnung)
 */
const GuenstigerpruefungTab: React.FC<GuenstigerpruefungTabProps> = ({
  portfolioPositionen,
  steuerParameter,
  jahreseinkommen,
  sonderausgaben,
  onJahreseinkommenUpdate,
  onSonderausgabenUpdate,
  sparerPauschbetragEnabled
}) => {

  // Berechne Kapitalerträge aus Portfolio
  const kapitalertraege = useMemo(() => {
    if (!portfolioPositionen.length || !steuerParameter) return null;

    const sparerPauschbetragBetrag = SPARERFREIBETRAEGE[steuerParameter.familienstand];
    let gesamtVerkaufserloes = 0;

    const positionsForTaxCalculation = [...portfolioPositionen].sort((a, b) => a.name.localeCompare(b.name));
    const rows = positionsForTaxCalculation
      .filter((position) => !position.exclude && position.sellPercentage > 0)
      .map((position) => {
        const verkaufsWert =
          position.quantity * (position.sellPercentage / 100) * position.currentPrice;
        gesamtVerkaufserloes += verkaufsWert;
        const fifoDetails = PortfolioService.calculateFIFOTaxableProfitWithDetails(
          position,
          position.sellPercentage
        );
        const fifoGewinn = fifoDetails.soldLots.reduce(
          (sum, lot) =>
            sum + PortfolioService.applyTaxTreatment(lot.profit, lot.effectiveTaxTreatment),
          0
        );
        return {
          positionKey: portfolioPositionKey(position),
          gewinn: fifoGewinn
        };
      });

    if (rows.length === 0) {
      return {
        verkaufserloes: gesamtVerkaufserloes,
        steuerpflichtigerGewinn: 0,
        kapitalertragsteuer: 0,
        solidaritaetszuschlag: 0,
        kirchensteuer: 0,
        gesamtsteuer: 0,
        verwendeterPauschbetrag: 0
      };
    }

    const net = berechneNettoKapitalsteuerNachVerrechnung(
      rows,
      steuerParameter,
      sparerPauschbetragEnabled,
      sparerPauschbetragBetrag
    );

    return {
      verkaufserloes: gesamtVerkaufserloes,
      steuerpflichtigerGewinn: net.steuerpflichtigerGewinnNachPausch,
      kapitalertragsteuer: net.kapitalertragsteuer,
      solidaritaetszuschlag: net.solidaritaetszuschlag,
      kirchensteuer: net.kirchensteuer,
      gesamtsteuer: net.gesamtsteuer,
      verwendeterPauschbetrag: net.verwendeterPauschbetrag
    };
  }, [portfolioPositionen, steuerParameter, sparerPauschbetragEnabled]);

  // Berechne das finale zu versteuernde Einkommen
  const zuVersteuerndesEinkommen = useMemo(() => {
    if (!kapitalertraege) return 0;
    const jahreseinkommenValue = parseFloat(jahreseinkommen) || 0;
    const sonderausgabenValue = parseFloat(sonderausgaben) || 0;
    const bruttoEinkommen = jahreseinkommenValue + kapitalertraege.steuerpflichtigerGewinn;
    return Math.max(0, bruttoEinkommen - sonderausgabenValue);
  }, [kapitalertraege, jahreseinkommen, sonderausgaben]);

  // Vergleichsberechnung
  const vergleichsrechnung = useMemo(() => {
    if (!kapitalertraege || !steuerParameter || !jahreseinkommen) return null;

    const jahreseinkommenValue = parseFloat(jahreseinkommen);
    const sonderausgabenValue = parseFloat(sonderausgaben) || 0;
    
    if (isNaN(jahreseinkommenValue) || jahreseinkommenValue < 0) return null;
    if (sonderausgabenValue < 0) return null;

    // Gesamteinkommen = reguläres Einkommen + steuerpflichtige Kapitalerträge
    const bruttoEinkommen = jahreseinkommenValue + kapitalertraege.steuerpflichtigerGewinn;
    
    // Zu versteuerndes Einkommen nach Abzug der Sonderausgaben
    const gesamtEinkommen = Math.max(0, bruttoEinkommen - sonderausgabenValue);

    // VARIANTE A: Kapitalertragsteuer + Einkommensteuer auf reguläres Einkommen
    const kapitalertragsteuerGesamt = kapitalertraege.gesamtsteuer;
    const regulaerEinkommen = Math.max(0, jahreseinkommenValue - sonderausgabenValue);
    const einkommensteuerAufRegulaerEinkommen = calculateOfficialIncomeTax(regulaerEinkommen, steuerParameter.familienstand, steuerParameter.kirchensteuer, steuerParameter.steuerjahr);
    const gesamtsteuerVarianteA = kapitalertragsteuerGesamt + einkommensteuerAufRegulaerEinkommen;

    // VARIANTE B: Einkommensteuer auf Gesamteinkommen (regulär + Kapitalerträge)
    const einkommensteuerGesamt = calculateOfficialIncomeTax(gesamtEinkommen, steuerParameter.familienstand, steuerParameter.kirchensteuer, steuerParameter.steuerjahr);

    // Ersparnis berechnen (Variante A vs. Variante B)
    const ersparnis = gesamtsteuerVarianteA - einkommensteuerGesamt;
    const istEinkommensteuerGuenstiger = ersparnis > 0;

    return {
      kapitalertragsteuerGesamt,
      einkommensteuerGesamt,
      gesamtsteuerVarianteA,
      einkommensteuerAufRegulaerEinkommen,
      regulaerEinkommen,
      ersparnis: Math.abs(ersparnis),
      istEinkommensteuerGuenstiger,
      empfehlung: istEinkommensteuerGuenstiger ? 'Gemeinsame Besteuerung (Einkommensteuer)' : 'Separate Besteuerung (Kapitalertragsteuer)',
      gesamtEinkommen,
      bruttoEinkommen,
      sonderausgabenValue
    };
  }, [kapitalertraege, jahreseinkommen, sonderausgaben, steuerParameter]);

  if (!portfolioPositionen.length || !portfolioPositionen.some(p => !p.exclude && p.sellPercentage > 0)) {
    return (
      <Container>
        <Alert type="info">
          <strong>Keine Verkäufe geplant</strong>
          <br />
          Der Modellvergleich ist nur verfügbar, wenn eine beispielhafte Verkaufsreihenfolge vorliegt.
        </Alert>
      </Container>
    );
  }

  if (!steuerParameter) {
    return (
      <Container>
        <Alert type="warning">
          <strong>Berechnungsparameter fehlen</strong>
          <br />
          Bitte vervollständigen Sie zunächst Schritt 1 (Berechnungsparameter).
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween direction="vertical" size="l">

        {/* Eingabe Jahreseinkommen */}
        <Container
          header={
            <Header variant="h2">
              Einkommen und Abzüge eingeben
            </Header>
          }
        >
            <FormField
              label="Zu versteuerndes Jahreseinkommen (ohne Kapitalerträge)"
            description="Ihr reguläres Einkommen ohne die Kapitalerträge aus dem Portfolio"
            >
              <Input
                value={jahreseinkommen}
                onChange={({ detail }) => onJahreseinkommenUpdate(detail.value)}
                placeholder="25000"
                type="number"
              />
            </FormField>

          <FormField
            label="Zu versteuernder Gewinn aus Portfolio-Verkäufen"
            description="Automatisch berechnet basierend auf Ihren geplanten Verkäufen in Schritt 3"
          >
            <div style={{ 
              color: '#000000'
            }}>
              <style dangerouslySetInnerHTML={{
                __html: `
                  .readonly-input-black input {
                    color: #000000 !important;
                  }
                `
              }} />
              <div className="readonly-input-black">
                <Input
                  value={kapitalertraege ? formatCurrency(kapitalertraege.steuerpflichtigerGewinn, 'EUR') : '0,00 €'}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </FormField>

          <FormField
            label="Sonderausgaben"
            description="Aufwendungen zur Basisversorgung (z.B. Altersvorsorge, Krankenversicherung)."
          >
            <Input
              value={sonderausgaben}
              onChange={({ detail }) => onSonderausgabenUpdate(detail.value)}
              placeholder="0"
              type="number"
              inputMode="numeric"
            />
          </FormField>

          <FormField
            label="Zu versteuerndes Einkommen (gesamt)"
            description="Jahreseinkommen + Portfolio-Gewinne - Sonderausgaben"
          >
            <div style={{ 
              color: '#000000'
            }}>
              <style dangerouslySetInnerHTML={{
                __html: `
                  .readonly-input-black-total input {
                    color: #000000 !important;
                  }
                `
              }} />
              <div className="readonly-input-black-total">
                <Input
                  value={formatCurrency(zuVersteuerndesEinkommen, 'EUR')}
                  readOnly
                  disabled
                />
              </div>
            </div>
          </FormField>


        </Container>

        {/* Einkommenssteuerberechnung */}
        <Container
          header={
            <Header variant="h2">
              Einkommenssteuerberechnung inkl. Steuerpflichtiger Gewinn aus Kapitaleinkünften
            </Header>
          }
          >
            <SpaceBetween direction="vertical" size="m">
              {(() => {
                if (!steuerParameter) return null;
                
                const steuerDetails = calculateOfficialIncomeTaxDetailed(zuVersteuerndesEinkommen, steuerParameter.familienstand, steuerParameter.kirchensteuer, steuerParameter.steuerjahr);
                // const grenzsteuersatz = Math.max(0, (einkommensteuerPlusEin - steuerDetails.gesamt) * 100);
                // const durchschnittssteuersatz = zuVersteuerndesEinkommen > 0 ? (steuerDetails.gesamt / zuVersteuerndesEinkommen * 100) : 0;

                return (
                  <>
                    <ColumnLayout columns={3}>
                      <Box>
                        <Box variant="awsui-key-label">Zu versteuerndes Einkommen</Box>
                        <Box fontSize="heading-m" color="text-status-info">
                          {formatCurrency(zuVersteuerndesEinkommen, 'EUR')}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Grundlage für Steuerberechnung
                        </Box>
                    </Box>

                      <Box>
                        <Box variant="awsui-key-label">Einkommensteuer gesamt</Box>
                        <Box fontSize="heading-m" color="text-status-error">
                          {formatCurrency(steuerDetails.gesamt, 'EUR')}
                    </Box>
                        <Box variant="small" color="text-body-secondary">
                          ESt + Soli + Kirchensteuer
                    </Box>
                    </Box>
                    
                      <Box>
                        <Box variant="awsui-key-label">Netto-Einkommen</Box>
                        <Box fontSize="heading-m" color="text-status-success">
                          {formatCurrency(zuVersteuerndesEinkommen - steuerDetails.gesamt, 'EUR')}
                        </Box>
                        <Box variant="small" color="text-body-secondary">
                          Nach Einkommensteuer
                        </Box>
                      </Box>
                    </ColumnLayout>

                    {/* Detailed tax breakdown */}
                    <ExpandableSection headerText="Steuerkomponenten Details" variant="footer">
                          <ColumnLayout columns={3}>
                            <Box>
                          <Box variant="awsui-key-label">Einkommensteuer</Box>
                              <Box fontSize="heading-s" color="text-status-info">
                            {formatCurrency(steuerDetails.einkommensteuer, 'EUR')}
                              </Box>
                          <Box variant="small" color="text-body-secondary">
                            Nach §32a EStG {steuerParameter?.steuerjahr ?? 2026}
                              </Box>
                            </Box>

                            <Box>
                              <Box variant="awsui-key-label">Solidaritätszuschlag</Box>
                              <Box fontSize="heading-s" color="text-status-info">
                            {formatCurrency(steuerDetails.solidaritaetszuschlag, 'EUR')}
                              </Box>
                          <Box variant="small" color="text-body-secondary">
                            {(() => {
                              if (!steuerParameter) return '5,5% der ESt';
                              const soliThresholds = steuerParameter.steuerjahr >= 2026 ? SOLI_THRESHOLDS_2026 : SOLI_THRESHOLDS_2025;
                              const soliFreigrenze = steuerParameter.familienstand === 'verheiratet' ? soliThresholds.FREIGRENZE_MARRIED : soliThresholds.FREIGRENZE_SINGLE;
                              const abschmelzungsObergrenze = soliFreigrenze * soliThresholds.GLEITZONE_MULTIPLIER;
                              const est = steuerDetails.einkommensteuer;
                              if (est <= soliFreigrenze) {
                                return `unter Freigrenze (ESt ≤ €${soliFreigrenze.toLocaleString('de-DE')}, §3 Abs. 3 SolZG)`;
                              }
                              if (est <= abschmelzungsObergrenze) {
                                return 'Gleitzone: 11,9% × (ESt − Freigrenze), §4 SolZG';
                              }
                              return '5,5% der ESt (volle Anwendung)';
                            })()}
                              </Box>
                            </Box>

                            <Box>
                              <Box variant="awsui-key-label">Kirchensteuer</Box>
                              <Box fontSize="heading-s" color="text-status-info">
                            {formatCurrency(steuerDetails.kirchensteuer, 'EUR')}
                              </Box>
                          <Box variant="small" color="text-body-secondary">
                            {(() => {
                              if (steuerParameter.kirchensteuer === 'keine') {
                                return 'Nicht anwendbar';
                              }
                              const kirchensteuerKonfig = KIRCHENSTEUER_OPTIONEN.find(k => k.key === steuerParameter.kirchensteuer);
                              return kirchensteuerKonfig ? `${kirchensteuerKonfig.prozentsatz}% der ESt` : 'Nicht anwendbar';
                            })()}
                          </Box>
                        </Box>
                      </ColumnLayout>
                    </ExpandableSection>
                  </>
                );
              })()}

            <ColumnLayout columns={2}>
              <Box>
                  <Box variant="awsui-key-label">Durchschnittssteuersatz</Box>
                  <Box fontSize="heading-l" color="text-status-info">
                    {(() => {
                      if (!steuerParameter) return '0.00 %';
                      const steuerDetails = calculateOfficialIncomeTaxDetailed(zuVersteuerndesEinkommen, steuerParameter.familienstand, steuerParameter.kirchensteuer, steuerParameter.steuerjahr);
                      const durchschnittssteuersatz = zuVersteuerndesEinkommen > 0 ? (steuerDetails.gesamt / zuVersteuerndesEinkommen * 100) : 0;
                      return durchschnittssteuersatz.toFixed(2) + ' %';
                    })()}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    Gesamtsteuer / Gesamteinkommen
                  </Box>
              </Box>
              
              <Box>
                  <Box variant="awsui-key-label">Grenzsteuersatz</Box>
                  <Box fontSize="heading-l" color="text-status-warning">
                    {(() => {
                      const estg = steuerParameter.steuerjahr >= 2026 ? ESTG_2026 : ESTG_2025;
                      let zvE = zuVersteuerndesEinkommen;
                      if (steuerParameter.familienstand === 'verheiratet') {
                        zvE = zuVersteuerndesEinkommen / 2;
                      }
                      
                      let grenzsteuersatz = 0;
                      if (zvE <= estg.GRUNDFREIBETRAG) {
                        grenzsteuersatz = 0;
                      } else if (zvE <= estg.ZONE2_OBERGRENZE) {
                        const y = (zvE - estg.GRUNDFREIBETRAG) / 10000;
                        grenzsteuersatz = (estg.ZONE2_KOEFF * 2 * y + 1400) / 100;
                      } else if (zvE <= estg.ZONE3_OBERGRENZE) {
                        const z = (zvE - estg.ZONE2_OBERGRENZE) / 10000;
                        grenzsteuersatz = (estg.ZONE3_KOEFF * 2 * z + 2397) / 100;
                      } else if (zvE <= estg.ZONE4_OBERGRENZE) {
                        grenzsteuersatz = 42;
                      } else {
                        grenzsteuersatz = 45;
                      }
                      
                      return Math.max(0, grenzsteuersatz).toFixed(2) + ' %';
                    })()}
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    Steuersatz auf den nächsten Euro
                  </Box>
              </Box>
            </ColumnLayout>

              <Alert type="info" header="Gemeinsame Besteuerung:">
                Diese detaillierte Berechnung zeigt alle Steuerkomponenten einzeln:
                <br/>• <strong>Einkommensteuer:</strong> Nach §32a EStG {steuerParameter?.steuerjahr ?? 2026} mit Ehegattensplitting
                <br/>• <strong>Solidaritätszuschlag:</strong> {(() => {
                  if (!steuerParameter) return '5,5% der ESt (ab hohem Einkommen)';
                  const soliThresholds = steuerParameter.steuerjahr >= 2026 ? SOLI_THRESHOLDS_2026 : SOLI_THRESHOLDS_2025;
                  const soliFreigrenze = steuerParameter.familienstand === 'verheiratet' ? soliThresholds.FREIGRENZE_MARRIED : soliThresholds.FREIGRENZE_SINGLE;
                  return `Freigrenze bezogen auf festgesetzte ESt: €${soliFreigrenze.toLocaleString('de-DE')} (§3 Abs. 3 SolZG); Gleitzone: 11,9% × (ESt − Freigrenze); darüber 5,5% der ESt`;
                })()}
                <br/>• <strong>Kirchensteuer:</strong> {(() => {
                  if (!steuerParameter || steuerParameter.kirchensteuer === 'keine') {
                    return 'Nicht anwendbar (keine Kirchensteuerpflicht)';
                  }
                  const kirchensteuerKonfig = KIRCHENSTEUER_OPTIONEN.find(k => k.key === steuerParameter.kirchensteuer);
                  return kirchensteuerKonfig ? `${kirchensteuerKonfig.prozentsatz}% der ESt` : 'Nicht anwendbar';
                })()}
                <br/><strong>Modellvergleich:</strong> Gesamtsteuer wird mit Kapitalertragsteuer (25% + Soli + Kirchensteuer) verglichen.
              </Alert>
            </SpaceBetween>
          </Container>

        {/* Einkommenssteuerberechnung und separate KapSteuer */}
          <Container
            header={
              <Header variant="h2">
              Einkommenssteuerberechnung und separate KapSteuer
              </Header>
            }
          >
          <SpaceBetween direction="vertical" size="m">
            {(() => {
              if (!steuerParameter || !kapitalertraege) return null;
              
              // Calculate income tax on regular income only (without capital gains)
              const jahreseinkommenValue = parseFloat(jahreseinkommen) || 0;
              const sonderausgabenValue = parseFloat(sonderausgaben) || 0;
              const regulaerEinkommen = Math.max(0, jahreseinkommenValue - sonderausgabenValue);
              const einkommensteuerRegular = calculateOfficialIncomeTax(regulaerEinkommen, steuerParameter.familienstand, steuerParameter.kirchensteuer, steuerParameter.steuerjahr);
              
              // Capital gains tax (separate)
              const kapitalertragsteuerGesamt = kapitalertraege.gesamtsteuer;
              
              // Total tax burden
              const gesamtsteuerSeparate = einkommensteuerRegular + kapitalertragsteuerGesamt;
              
              return (
                <>
                  <ColumnLayout columns={3}>
                    <Box>
                      <Box variant="awsui-key-label">Jahreseinkommen (ohne Kapitalerträge)</Box>
                      <Box fontSize="heading-m" color="text-status-info">
                        {formatCurrency(regulaerEinkommen, 'EUR')}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Einkommen minus Sonderausgaben
                      </Box>
                </Box>
                
                  <Box>
                      <Box variant="awsui-key-label">Einkommensteuer (nur Einkommen)</Box>
                      <Box fontSize="heading-m" color="text-status-error">
                        {formatCurrency(einkommensteuerRegular, 'EUR')}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        ESt + Soli + Kirchensteuer
                      </Box>
                    </Box>
                    
                    <Box>
                      <Box variant="awsui-key-label">Netto-Einkommen</Box>
                      <Box fontSize="heading-m" color="text-status-success">
                        {formatCurrency(regulaerEinkommen - einkommensteuerRegular, 'EUR')}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                        Nach Einkommensteuer
                      </Box>
                    </Box>
                  </ColumnLayout>

                  {/* Steuerkomponenten Details for income tax */}
                  <ExpandableSection headerText="Steuerkomponenten Details (Einkommensteuer)" variant="footer">
                    {(() => {
                      const steuerDetailsRegular = calculateOfficialIncomeTaxDetailed(regulaerEinkommen, steuerParameter.familienstand, steuerParameter.kirchensteuer, steuerParameter.steuerjahr);
                      return (
                        <ColumnLayout columns={3}>
                          <Box>
                            <Box variant="awsui-key-label">Einkommensteuer</Box>
                            <Box fontSize="heading-s" color="text-status-info">
                              {formatCurrency(steuerDetailsRegular.einkommensteuer, 'EUR')}
                            </Box>
                            <Box variant="small" color="text-body-secondary">
                              Nach §32a EStG {steuerParameter?.steuerjahr ?? 2026}
                            </Box>
                          </Box>

                          <Box>
                            <Box variant="awsui-key-label">Solidaritätszuschlag</Box>
                            <Box fontSize="heading-s" color="text-status-info">
                              {formatCurrency(steuerDetailsRegular.solidaritaetszuschlag, 'EUR')}
                            </Box>
                            <Box variant="small" color="text-body-secondary">
                              {(() => {
                                const soliThresholds = steuerParameter.steuerjahr >= 2026 ? SOLI_THRESHOLDS_2026 : SOLI_THRESHOLDS_2025;
                                const soliFreigrenze = steuerParameter.familienstand === 'verheiratet' ? soliThresholds.FREIGRENZE_MARRIED : soliThresholds.FREIGRENZE_SINGLE;
                                const abschmelzungsObergrenze = soliFreigrenze * soliThresholds.GLEITZONE_MULTIPLIER;
                                const est = steuerDetailsRegular.einkommensteuer;
                                if (est <= soliFreigrenze) {
                                  return `unter Freigrenze (ESt ≤ €${soliFreigrenze.toLocaleString('de-DE')}, §3 Abs. 3 SolZG)`;
                                }
                                if (est <= abschmelzungsObergrenze) {
                                  return 'Gleitzone: 11,9% × (ESt − Freigrenze), §4 SolZG';
                                }
                                return '5,5% der ESt (volle Anwendung)';
                              })()}
                    </Box>
                  </Box>
                  
                  <Box>
                            <Box variant="awsui-key-label">Kirchensteuer</Box>
                            <Box fontSize="heading-s" color="text-status-info">
                              {formatCurrency(steuerDetailsRegular.kirchensteuer, 'EUR')}
                            </Box>
                            <Box variant="small" color="text-body-secondary">
                              {(() => {
                                if (steuerParameter.kirchensteuer === 'keine') {
                                  return 'Nicht anwendbar';
                                }
                                const kirchensteuerKonfig = KIRCHENSTEUER_OPTIONEN.find(k => k.key === steuerParameter.kirchensteuer);
                                return kirchensteuerKonfig ? `${kirchensteuerKonfig.prozentsatz}% der ESt` : 'Nicht anwendbar';
                              })()}
                            </Box>
                          </Box>
                        </ColumnLayout>
                      );
                    })()}
                  </ExpandableSection>
                  
                  <ColumnLayout columns={3}>
                    <Box>
                      <Box variant="awsui-key-label">Kapitalertragsteuer (separate)</Box>
                      <Box fontSize="heading-m" color="text-status-warning">
                        {formatCurrency(kapitalertragsteuerGesamt, 'EUR')}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        {(() => {
                          // Show detailed breakdown based on church tax setting
                          if (steuerParameter?.kirchensteuer === 'baden-wuerttemberg-bayern') {
                            return 'KapESt (24.51%) + SolZ (1.35%) + KiSt (1.96%)';
                          } else if (steuerParameter?.kirchensteuer === 'andere-bundeslaender') {
                            return 'KapESt (24.45%) + SolZ (1.34%) + KiSt (2.20%)';
                          } else {
                            return 'KapESt (25.00%) + SolZ (1.38%) + KiSt (0.00%)';
                          }
                        })()}
                      </Box>
                    </Box>

                    <Box>
                      <Box variant="awsui-key-label">Gesamtsteuerbelastung (separate)</Box>
                      <Box fontSize="heading-m" color="text-status-error">
                        {formatCurrency(gesamtsteuerSeparate, 'EUR')}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                        Einkommensteuer + Kapitalertragsteuer
                      </Box>
                    </Box>
                    
                    <Box>
                      <Box variant="awsui-key-label">Netto nach allen Steuern</Box>
                      <Box fontSize="heading-m" color="text-status-success">
                        {formatCurrency(zuVersteuerndesEinkommen - gesamtsteuerSeparate, 'EUR')}
                      </Box>
                      <Box variant="small" color="text-body-secondary">
                        Gesamteinkommen minus alle Steuern
                    </Box>
                  </Box>
                </ColumnLayout>

                  <Alert type="info">
                    <strong>Separate Besteuerung:</strong> Diese Berechnung zeigt die getrennte Besteuerung von 
                    Arbeitseinkommen (Einkommensteuer inkl. Soli + Kirchensteuer) und Kapitalerträgen (Kapitalertragsteuer 25% + Soli + Kirchensteuer). 
                    Im Vergleich zur gemeinsamen Besteuerung (oben) können sich unterschiedliche Gesamtbelastungen ergeben.
                  </Alert>
                </>
              );
            })()}
              </SpaceBetween>
          </Container>

        {/* Modellvergleich Ergebnis - KPI Dashboard */}
        {vergleichsrechnung && (
          <Container
            header={
              <Header variant="h2">
                Modellvergleich: Einkommensteuer vs. Abgeltungsteuer (Beispielrechnung)
              </Header>
            }
          >
            <SpaceBetween direction="vertical" size="l">
              {/* Main Result KPI */}
              <Alert
                type="warning"
                header="Wichtiger rechtlicher Hinweis:"
              >
                <SpaceBetween direction="vertical" size="s">
                  <Box fontSize="body-m">
                    Diese Darstellung basiert auf einem stark vereinfachten Rechenmodell und dient ausschließlich der theoretischen Veranschaulichung möglicher Steuerwirkungen.
                  </Box>
                  <Box fontSize="body-m">
                    Die gezeigten Werte stellen kein Ergebnis einer individuellen steuerlichen Prüfung dar und dürfen nicht als Grundlage für steuerliche Entscheidungen verwendet werden.
                  </Box>
                  <Box fontSize="body-m">
                    <strong>Bitte beachten Sie:</strong>
                  </Box>
                  <Box fontSize="body-m" margin={{ left: 's' }}>
                    • Die berechneten Beträge können deutlich von den tatsächlichen steuerlichen Verhältnissen abweichen.
                  </Box>
                  <Box fontSize="body-m" margin={{ left: 's' }}>
                    • Ob und in welchem Umfang Unterschiede bei der Besteuerung (z. B. zwischen Kapitalertragsteuer und Einkommensteuer) in Ihrem Fall relevant sind, ist individuell zu prüfen.
                  </Box>
                  <Box fontSize="body-m" margin={{ left: 's' }}>
                    • Für eine verbindliche steuerliche Bewertung wenden Sie sich bitte an eine Steuerberaterin oder einen Steuerberater.
                  </Box>
                </SpaceBetween>
              </Alert>

              {/* KPI Comparison */}
              <ColumnLayout columns={2}>
                <div style={{ padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px', textAlign: 'center' }}>
                  <Box variant="awsui-key-label" margin={{ bottom: 's' }}>Gemeinsame Besteuerung</Box>
                  <Box 
                    fontSize="display-l" 
                    color={vergleichsrechnung.istEinkommensteuerGuenstiger ? "text-status-success" : "text-status-error"}
                    margin={{ bottom: 's' }}
                  >
                      {formatCurrency(vergleichsrechnung.einkommensteuerGesamt, 'EUR')}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                    ESt auf Gesamteinkommen
                    </Box>
                </div>

                <div style={{ padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px', textAlign: 'center' }}>
                  <Box variant="awsui-key-label" margin={{ bottom: 's' }}>Separate Besteuerung</Box>
                  <Box 
                    fontSize="display-l" 
                    color={vergleichsrechnung.istEinkommensteuerGuenstiger ? "text-status-error" : "text-status-success"}
                    margin={{ bottom: 's' }}
                  >
                    {formatCurrency(vergleichsrechnung.gesamtsteuerVarianteA, 'EUR')}
                    </Box>
                    <Box variant="small" color="text-body-secondary">
                    KapSt + ESt (reguläres Einkommen)
                    </Box>
                </div>


              </ColumnLayout>


            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default GuenstigerpruefungTab;
